<template>
  <div class="slideout-wrapper">
    <transition name="fade">
      <div class="overlay" v-if="show"></div>
    </transition>
    <transition name="slide">
      <div class="modal" v-if="show">
        <Header
          :title="slideOutTitle"
          :displayTabs="displayTabs"
          :currentTabIndex="currentTabIndex"
          v-bind:canSave="canSave"
          @cancel="cancel"
          @complete="complete()"
          @handleTabClick="$emit('handleTabClick', $event)"/>
        <Content
          :selectedProps="selectedProps"
          :portfolioBrands="portfolioBrands"
          :showFilters="showFilters"
          :availableInsightsDimensions="availableInsightsDimensions"
          :availableShopperIntelligenceDimensions="availableShopperIntelligenceDimensions"
          :brandIds="brandIds"
          :isAllSegmentsInactive="isAllSegmentsInactive"
          @setDimension="setDimension($event)"
          @handleIncludeFullSegments="handleIncludeFullSegments($event)"
          @updateMetricsFromConfig="updateMetricsFromConfig()"
          @updateBrands="updateBrands($event)"
          @updateMetricsFromPreview="updateMetricsFromPreview($event)"
          @removeFilterFromPreview="removeBasicFilterFromPreview($event)"
          @removeAdvancedFiltersFromPreview="removeAllAdvancedFiltersFromPreview"
          @updateBasicFilters="updateBasicFilters($event)"
          @updateAdvancedFilters="updateAdvancedFilters($event)"
          @addFilters="$emit('handleTabClick', 1)"
          @selectFilterType="selectFilterType($event)"
        />
      </div>
    </transition>
  </div>
</template>
<script>
import { mapGetters, mapState } from 'vuex';
import Header from './Header.vue';
import Content from './Content/ContentWrapper.vue';
import {
  getReportFieldFromValue,
  getReportFieldsFromValues,
  getDemographicsFieldName,
} from '../../../../services/dashboard/reportFieldService';
import { getAvailableReportFields } from '../../../../services/dashboard/exclusionsService';

export default {
  props: {
    show: {
      type: Boolean,
      required: true,
    },
    portfolioBrands: {
      type: Array,
      required: true,
    },
    selectedFilters: Array,
    selectedMetrics: Array,
    selectedDimension: String,
    newReportId: Boolean,
    selectedFilterType: String,
    includeFullSegments: Boolean,
    slideOutTab: Number,
  },
  components: {
    Header,
    Content,
  },
  data() {
    return {
      slideOutTitle: 'Build Report',
      displayTabs: ['Dimension & Metrics', 'Filters'],
      demographicsMetricsValues: [
        'purchaseIntentClicks',
        'purchaseIntentRate',
        'pageViews',
      ],
      selectedProps: {
        brands: [],
        dimension: '',
        includeFullSegments: false,
        metricsList: [],
        metrics: [],
        basicFilters: [],
        advancedFilters: [],
        filterType: 'Basic',
      },
      defaultShopperDimensionsValues: [
        'age',
        'education',
        'ethnicity',
        'gender',
        'householdIncome',
        'languagePreference',
        'maritalStatus',
        'occupation',
        'religion',
        'stateOfResidence',
      ],
      sortedShopperCategories: [
        'Demographics',
        'Household',
        'Lifestyles & Interests',
        'Home & Real Estate',
        'Charitable Donations',
        'Epsilon Niches',
      ],
    };
  },
  computed: {
    ...mapState({
      storedIncludeFullSegments: state =>
        state.dashboard.reportRequest.demographicsIncludeFullSegments,
      dimensions: state => state.dashboard.availableReportFields.dimensions,
      demographicsDimensions: state => state.dashboard.availableReportFields.demographicsDimensions,
      allSegments: state => state.dashboard.availableReportFields.allSegmentsDimension,
      allReportFields: state => state.dashboard.availableReportFields.allReportFields,
      allInsightsAllowedFilters: state => state.filters.allowedFilters.allInsights,
    }),
    ...mapGetters([
      'selectedBrands',
    ]),
    canSave() {
      return !!(this.selectedProps.dimension.length &&
        this.selectedProps.metrics.length &&
        this.selectedProps.brands.length &&
        this.canSaveFilters);
    },
    canSaveFilters() {
      return this.selectedProps.filterType === 'Basic'
        ? this.basicFiltersComplete
        : this.advancedFiltersComplete;
    },
    basicFiltersComplete() {
      return this.filtersComplete(this.selectedProps.basicFilters);
    },
    advancedFiltersComplete() {
      return this.selectedProps.advancedFilters.every(filters => {
        const cleanedFilters = filters.filter(f => f.key);

        return this.filtersComplete(cleanedFilters);
      });
    },
    currentTabIndex() {
      return this.slideOutTab;
    },
    showFilters() {
      return this.currentTabIndex === 1;
    },
    insightsDimensions() {

      return this.allInsightsAllowedFilters.map(val => {
        return {
          ...val,
          section: 'mikmakInsights',
        };
      });
    },
    shopperIntelligenceAllSegmentsDimension() {
      return [ this.allSegments ];
    },
    isAllSegmentsInactive() {
      const parameters = getAvailableReportFields(
        this.shopperIntelligenceAllSegmentsDimension,
        [],
        this.localFilterDimensions,
        this.selectedProps.metrics,
      );

      return parameters[0].disabled;
    },
    availableInsightsDimensions() {
      return getAvailableReportFields(
        this.insightsDimensions,
        this.localAllSegmentsDimension,
        this.localFilterDimensions,
        this.selectedProps.metrics,
      );
    },
    availableShopperIntelligenceDimensions() {
      return this.sortedShopperCategories.reduce((acc, category) => {
        const dimensions = this.demographicsDimensions
          .filter(dim => dim.category === category);

        dimensions.forEach(val => val.section = 'mikmakShopperIntelligence');

        acc = acc.concat(this.sortedDimensions(dimensions));

        return acc;
      }, []);
    },
    localFilterDimensions() {
      return this.selectedProps.filterType === 'Basic'
        ? getReportFieldsFromValues(
          this.allReportFields,
          this.selectedProps.basicFilters.map(f => f.key))
        : getReportFieldsFromValues(
          this.allReportFields,
          this.selectedProps.advancedFilters.flat().map(f => f.key));
    },
    localAllSegmentsDimension() {
      return this.selectedProps.dimension === 'allSegments'
        ? this.shopperIntelligenceAllSegmentsDimension
        : [];
    },
    brandIds() {
      return this.selectedProps.brands.map(pb => pb.value);
    },
  },
  methods: {
    updateMetricsList() {
      this.selectedProps.metricsList =
        getAvailableReportFields(
          this.selectedProps.metricsList,
          this.localAllSegmentsDimension,
          this.localFilterDimensions,
          this.selectedProps.metrics,
        );
    },
    filtersComplete(filters) {
      return filters.every(filter => {
        return filter.value &&
          filter.value.length > 0 &&
          filter.operator &&
          filter.key;
      });
    },
    sortedDimensions(dimensions) {
      const dimensionsObj = dimensions.reduce((acc, dim) => {
        if (this.defaultShopperDimensionsValues.includes(dim.value)) acc.default.push(dim);
        else acc.extra.push(dim);

        return acc;
      }, { default: [], extra: [] });

      const sortedDefaultDimensions = this.displayDimensionsInColumns(dimensionsObj.default);
      const sortedExtraDimensions = this.displayDimensionsInColumns(dimensionsObj.extra);

      return sortedDefaultDimensions.concat(sortedExtraDimensions);
    },
    displayDimensionsInColumns(dimensions) {
      const dimensionsSorted = dimensions.sort((a, b) => {
        const aName = a.value.toLowerCase();
        const bName = b.value.toLowerCase();

        return aName < bName ? -1 : 1;
      });

      const half = Math.ceil(dimensionsSorted.length / 2);
      const firsHalf = dimensionsSorted.slice(0, half);
      const secondHalf = dimensionsSorted.slice(half);

      return firsHalf.reduce((acc, val, index) => {
        acc.push(val);
        if (secondHalf[index]) acc.push(secondHalf[index]);

        return acc;
      }, []);
    },
    getDemographicsMetrics() {
      return this.demographicsMetricsValues
        .map(value => {
          const reportField = getReportFieldFromValue(this.allReportFields, value);
          const key = getDemographicsFieldName(reportField);

          return { ...reportField, key };
        });
    },
    cancel() {
      this.$emit('cancel');
    },
    complete() {
      const reshapedFilters = this.reshapeFilters();

      this.$emit('complete', {
        brands: this.selectedProps.brands,
        dimension: this.selectedProps.dimension,
        metrics: this.selectedProps.metrics,
        includeFullSegments: this.selectedProps.includeFullSegments,
        filters: reshapedFilters,
        filterType: this.selectedProps.filterType,
      });
    },
    reshapeFilters() {
      const filters = this.selectedProps.filterType === 'Basic' ?
        [this.selectedProps.basicFilters] :
        this.selectedProps.advancedFilters;

      return filters.reduce((acc, filterGroup) => {
        const cleanFilterGroup = this.removeEmptyFilters(filterGroup);

        if (cleanFilterGroup && cleanFilterGroup.length > 0) {
          acc.push({
            filters: cleanFilterGroup,
          });
        }

        return acc;
      }, []);
    },
    removeEmptyFilters(filterGroup) {
      return filterGroup.reduce((acc, filter) => {
        if (this.isValidFilter(filter)) {
          acc.push(filter);
        }

        return acc;
      }, []);
    },
    isValidFilter(filter) {
      return filter &&
        filter.key &&
        filter.operator &&
        filter.value &&
        filter.value.length > 0;
    },
    setDimension(dimension) {
      this.selectedProps.dimension = dimension;
    },
    handleIncludeFullSegments(val) {
      this.selectedProps.includeFullSegments = val;
      this.updateMetricsList();
    },
    updateMetricsFromConfig() {
      const newSelectedMetrics = this.selectedProps.metricsList
        .filter(metric => metric.checked);
      const oldSelectedMetrics = this.selectedProps.metrics;

      const onlyNewMetrics = newSelectedMetrics
        .filter( metric => !oldSelectedMetrics.find(m => m.key === metric.key));

      const onlyExistingMetrics = oldSelectedMetrics
        .filter( metric => newSelectedMetrics.find(m => m.key === metric.key));

      this.selectedProps.metrics = onlyExistingMetrics.concat(onlyNewMetrics);

      this.updateMetricsList();
    },
    updateBrands(brands) {
      this.selectedProps.brands = brands;
      this.removeAllBasicFiltersFromPreview();
      this.removeAllAdvancedFiltersFromPreview();
    },
    updateMetricsFromPreview(selectedMetrics) {
      this.selectedProps.metrics = selectedMetrics;
      this.selectedProps.metricsList.forEach(metric => {
        const metricSelected = selectedMetrics.find(m => m.key === metric.key);

        metric.checked = !!metricSelected;
      });

      this.updateMetricsList();
    },
    updateMetricsFromReport() {
      this.selectedProps.metricsList.forEach(metric => {
        const metricSelected = (this.selectedMetrics || [])
          .find(m => m === metric.value);

        metric.checked = !!metricSelected;
      });

      const checkedMetrics = this.selectedProps.metricsList
        .filter(metric => metric.checked);

      return checkedMetrics.sort((a, b) => {
        return this.selectedMetrics.indexOf(a.value) - this.selectedMetrics.indexOf(b.value);
      });

    },
    removeBasicFilterFromPreview(removedFilter) {
      const index = this.selectedProps.basicFilters
        .findIndex(filter => filter.key === removedFilter.key);
      const left = this.selectedProps.basicFilters.slice(0, index);
      const right = this.selectedProps.basicFilters.slice(index+1);

      this.selectedProps.basicFilters = left.concat(right);

      this.updateMetricsList();
    },
    removeAllBasicFiltersFromPreview() {
      this.selectedProps.basicFilters = [];
    },
    removeAllAdvancedFiltersFromPreview() {
      const emptyFilter = {
        key: '',
        operator: '',
        value: [],
      };

      this.selectedProps.advancedFilters = [[emptyFilter]];
      this.updateMetricsList();
    },
    updateBasicFilters(basicFilters) {
      this.selectedProps.basicFilters = basicFilters;
      this.updateMetricsList();
    },
    updateAdvancedFilters(advancedFilters) {
      this.selectedProps.advancedFilters = advancedFilters;
      this.updateMetricsList();
    },
    selectFilterType(val) {
      this.selectedProps.filterType = val;
      this.updateMetricsList();
    },
    updateSelectedFilters(selectedFilters) {
      const useBasicFilter = this.selectedProps.filterType === 'Basic';
      const selectedFilterCopy = selectedFilters.map(filterGroup => {
        return filterGroup.filters.map(f => {
          const filterCopy = { ...f };

          filterCopy.value = [...f.value];

          return filterCopy;
        });
      });

      this.selectedProps.basicFilters = useBasicFilter ?
        selectedFilterCopy[0] || [] : [];
      this.selectedProps.advancedFilters = !useBasicFilter ?
        selectedFilterCopy : [];
    },
    updateParameters() {
      this.selectedProps.brands = [...this.selectedBrands] || [];
      this.selectedProps.metrics = this.updateMetricsFromReport();
      this.selectedProps.dimension = this.selectedDimension;
      this.selectedProps.filterType = this.selectedFilterType;
      this.selectedProps.includeFullSegments = this.includeFullSegments;
      const selectedFilters = this.selectedFilters || [];

      this.updateSelectedFilters(selectedFilters);
      this.updateMetricsList();
    },
  },
  created() {
    const metricsList = this.getDemographicsMetrics();

    this.selectedProps.metricsList = metricsList.map((metric, index) => {
      metric.id = 'metric-' + index;
      const metricIndex = (this.selectedMetrics || [])
        .findIndex(m => m === metric.value);

      metric.checked = metricIndex >= 0;

      return metric;
    });
    this.updateParameters();
  },
  watch: {
    newReportId() {
      if (this.newReportId) {
        this.updateParameters();
        this.$emit('offNewReportId');
      }
    },
    selectedFilters() {
      const selectedFilters = this.selectedFilters || [];

      this.updateSelectedFilters(selectedFilters);
    },
    storedIncludeFullSegments() {
      this.handleIncludeFullSegments(
        this.storedIncludeFullSegments ||
        this.selectedProps.includeFullSegments,
      );
    },
    selectedBrands() {
      this.selectedProps.brands = [...this.selectedBrands];
    },
  },
};
</script>

<style scoped lang="scss">
.slideout-wrapper {
  font-family: Lato;
  font-style: normal;
  font-weight: normal;
  font-size: 16px;
  line-height: 125%;
  color: #223448;
}
.overlay {
  z-index: 2;
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: rgba(0, 0, 0, 0.5);
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
.modal {
  z-index: 2;
  background-color: #FFFFFF;
  position: fixed;
  width: 100%;
  right: 0px;
  top: 0px;
}
.slideout-wrapper .modal {
  overflow: auto;
  text-align: left;
  top: 0;
  bottom: 0;
  right: 0;
  padding-bottom: 92px;
  width: 75%;
}
.slide-enter-active,
.slide-leave-active {
  transition: transform 0.2s ease;
}
.slide-enter,
.slide-leave-to {
  transform: translateX(100%);
  transition: all 150ms ease-in 0s;
}
</style>
